import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, DollarSign, ArrowUp, Edit, ArrowLeft } from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import PreContractService from '../../services/preContractService';

const ViewDoA = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { error: showError, toasts, removeToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [rule, setRule] = useState(null);

  useEffect(() => {
    const fetchRule = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const response = await PreContractService.getDoA(id);
        if (response?.data) {
          setRule(response.data);
        } else {
          showError('DoA rule not found');
          navigate('/contracts/pre-contract/doa');
        }
      } catch (err) {
        console.error(err);
        showError('Failed to load DoA rule');
        navigate('/contracts/pre-contract/doa');
      } finally {
        setLoading(false);
      }
    };
    fetchRule();
  }, [id, navigate, showError]);

  const headerActions = [
    { label: 'Back', icon: <ArrowLeft size={18} />, onClick: () => navigate('/contracts/pre-contract/doa'), variant: 'outline' },
    { label: 'Edit', icon: <Edit size={18} />, onClick: () => navigate(`/contracts/pre-contract/doa/edit/${id}`), variant: 'primary' },
  ];

  if (loading) {
    return (
      <div className="page-container d-flex align-items-center justify-content-center min-vh-50">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!rule) return null;

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="DoA Rule Details"
        description="View delegation of authority rule"
        actions={headerActions}
      />
      <div className="page-container">
        <div className="card shadow-sm border-0">
          <div className="card-body p-4">
            <div className="row g-4">
              <div className="col-12">
                <label className="form-label text-muted small text-uppercase">Role / Title</label>
                <div className="d-flex align-items-center gap-2">
                  <Users size={18} className="text-primary" />
                  <span className="fw-semibold fs-6">{rule.role || '—'}</span>
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label text-muted small text-uppercase">Max Approval Limit</label>
                <div className="d-flex align-items-center gap-2">
                  <DollarSign size={18} className="text-success" />
                  <span className="fw-semibold">{rule.currency} {Number(rule.approvalLimit || 0).toLocaleString()}</span>
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label text-muted small text-uppercase">Rank</label>
                <div className="d-flex align-items-center gap-2">
                  <ArrowUp size={18} className="text-secondary" />
                  <span className="badge bg-light text-dark border">Level {rule.rank ?? '—'}</span>
                </div>
              </div>
              <div className="col-12">
                <label className="form-label text-muted small text-uppercase">Status</label>
                <span className={`badge ${rule.isActive ? 'bg-success' : 'bg-secondary'}`}>
                  {rule.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default ViewDoA;
